/**
 * 1.81.0 · Puesto: useQuery y useStatus contra la base del servidor. Igual que los de PowerSync:
 * `{ data, isLoading }` y se vuelven a leer cuando el servidor avisa que cambió alguna de sus tablas.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { getClient, onLive, onTablesChanged, puestoState, serverCall } from "./puesto";
import { tablesIn } from "./db-host";
import { TABLE_NAMES } from "@/offline/schema";

const KNOWN = new Set(TABLE_NAMES);

export function useRemoteQuery<T = unknown>(sql: string, params: unknown[] = []): { data: T[]; isLoading: boolean; error?: Error } {
  const [state, setState] = useState<{ data: T[]; isLoading: boolean; error?: Error }>({ data: [], isLoading: true });
  const key = JSON.stringify([sql, params]);
  const tables = useMemo(() => new Set(tablesIn(sql, KNOWN)), [sql]);
  const run = useRef(0);
  useEffect(() => {
    let alive = true;
    let again = false;
    let busy = false;
    // 1.99.1: se suscribe en el servidor: él vuelve a correr la consulta cuando cambia algo y manda el resultado
    // solo si cambió. Si el servidor no sabe suscribir (versión anterior), se pide como antes con cada cambio.
    const sid = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    let live = true;
    let v = 0;
    const offLive = onLive(sid, (m) => {
      if (!alive || m.v <= v) return;
      v = m.v;
      if (m.rows) setState({ data: m.rows as T[], isLoading: false });
      else setState((s) => ({ data: s.data, isLoading: false, error: new Error(m.e ?? "Error") }));
    });
    const load = async () => {
      if (busy) { again = true; return; }
      busy = true;
      const n = ++run.current;
      try {
        const c = await getClient();
        if (!alive) return; // la pantalla se cerró mientras se esperaba la conexión
        if (live) {
          try {
            const r = await c.call<{ v: number; rows: T[] }>("sub", { s: sid, sql, params });
            // Se cerró mientras tanto: se avisa por la misma conexión (si no, quedaría suscripta en el servidor).
            if (!alive) { void c.call("unsub", { s: sid }, 5_000).catch(() => undefined); return; }
            if (n === run.current) { v = r.v; setState({ data: (r.rows ?? []) as T[], isLoading: false }); }
            return;
          } catch (e) {
            if (!/Pedido desconocido/.test((e as Error)?.message ?? "")) throw e;
            live = false; // servidor viejo
          }
        }
        const rows = ((await c.call<T[]>("q", { sql, params })) ?? []) as T[];
        if (alive && n === run.current) setState({ data: rows, isLoading: false });
      } catch (e) {
        if (alive && n === run.current) setState((s) => ({ data: s.data, isLoading: false, error: e as Error }));
      } finally {
        busy = false;
        if (again && alive) { again = false; void load(); }
      }
    };
    setState((s) => ({ data: s.data, isLoading: true }));
    void load();
    const off = onTablesChanged((changed) => {
      // null = se reconectó: hay que volver a suscribirse. Con suscripción, los cambios llegan solos.
      if (!changed) { void load(); return; }
      if (!live && (!tables.size || changed.some((t) => tables.has(t)))) void load();
    });
    return () => {
      alive = false; off(); offLive();
      if (live) void getClientIfReady()?.call("unsub", { s: sid }).catch(() => undefined);
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

/** El cliente, solo si ya está conectado (para avisar sin esperar a una reconexión). */
function getClientIfReady() {
  return puestoState.get().state === "ready" ? { call: (m: string, a: unknown) => getClient().then((c) => c.call(m, a, 5_000)) } : null;
}

export interface RemoteStatus {
  connected: boolean;
  lastSyncedAt?: Date;
  dataFlowStatus: { uploading?: boolean; downloading?: boolean; uploadError?: Error; downloadError?: Error };
  hasSynced?: boolean;
}

let lastStatus: RemoteStatus = { connected: false, dataFlowStatus: {} };
export const remoteStatus = () => lastStatus;

/** Estado de la sincronización del servidor con la nube (lo que ve el puesto). */
export function useRemoteStatus(): RemoteStatus {
  const [s, setS] = useState<RemoteStatus>(lastStatus);
  useEffect(() => {
    let alive = true;
    const pull = async () => {
      if (puestoState.get().state !== "ready") return;
      try {
        const r = await serverCall<{ connected: boolean; lastSyncedAt: string | null; uploading: boolean; downloading: boolean; hasSynced?: boolean }>("status");
        lastStatus = {
          connected: !!r?.connected,
          lastSyncedAt: r?.lastSyncedAt ? new Date(r.lastSyncedAt) : undefined,
          dataFlowStatus: { uploading: !!r?.uploading, downloading: !!r?.downloading },
          hasSynced: r?.hasSynced,
        };
        if (alive) setS(lastStatus);
      } catch { /* sin servidor: lo muestra la pantalla de «Sin servidor» */ }
    };
    void pull();
    const t = setInterval(() => void pull(), 5_000);
    const off = puestoState.subscribe(() => void pull());
    return () => { alive = false; clearInterval(t); off(); };
  }, []);
  return s;
}
